import { useMemo } from "react";

const LABEL_MAP = {
  joyful: "Joyful",
  excited: "Excited",
  happy: "Happy",
  amused: "Amused",
  content: "Content",
  interested: "Interested",
  sad: "Sad",
  crying: "Upset",
  angry: "Angry",
  frustrated: "Frustrated",
  disgusted: "Disgusted",
  contemptuous: "Skeptical",
  fearful: "Fearful",
  anxious: "Anxious",
  surprise: "Surprised",
  surprised: "Surprised",
  shocked: "Shocked",
  confused: "Confused",
  skeptical: "Curious",
  stressed: "Stressed",
  bored: "Bored",
  tired: "Tired",
  concentrated: "Focused",
  focus: "Focused",
  focused: "Focused",
  yawn: "Yawn",
  serious: "Serious",
  neutral: "Neutral",
  engaged: "Engaged",
  unengaged: "Unengaged",
  distracted: "Distracted",
  out_of_frame: "No Face Detected",
};

const getLabel = (value) => LABEL_MAP[value] || "Sensing";

export default function EmotionIndicatorBadge({
  emotion,
  confidence,
  cognitive,
  heartRateBpm,
  isDetecting,
  activeColor,
  isCalibrating,
  calibrationPercent,
}) {
  const emotionLabel = useMemo(() => {
    if (isCalibrating) {
      if (typeof calibrationPercent === "number") return `Calibrating ${calibrationPercent}%`;
      return "Calibrating";
    }
    // Prioritize out_of_frame cognitive state as the main label
    if (cognitive === "out_of_frame") return getLabel("out_of_frame");
    if (emotion) return getLabel(emotion);
    return "Detecting";
  }, [emotion, cognitive, isCalibrating, calibrationPercent]);

  const confidenceText = typeof confidence === "number"
    ? `${Math.round(Math.max(0, Math.min(1, confidence)) * 100)}% confidence`
    : null;
  const heartRateText = typeof heartRateBpm === "number" && Number.isFinite(heartRateBpm) && heartRateBpm > 0
    ? `${Math.round(heartRateBpm)} bpm`
    : null;

  if (!isDetecting && !emotion && !isCalibrating) return null;

  return (
    <>
      <style>{`@keyframes eieHaloPulse { 0% { opacity: 0.12; transform: scale(0.98); } 50% { opacity: 0.3; transform: scale(1.02); } 100% { opacity: 0.12; transform: scale(0.98); } }
@keyframes eieHeartBeat { 0%, 100% { transform: scale(1); } 20% { transform: scale(1.2); } 40% { transform: scale(0.96); } 60% { transform: scale(1.14); } 80% { transform: scale(1); } }`}</style>
      <div style={styles.wrapper} aria-live="polite">
      <div
        style={{
          ...styles.halo,
          backgroundColor: activeColor,
          opacity: isDetecting ? 0.2 : 0.12,
          animation: "eieHaloPulse 1.8s ease-in-out infinite",
          animationPlayState: isDetecting ? "running" : "paused",
        }}
      />

      <div style={styles.badgeClip}>
        <div style={styles.blurBackground} />
        <div style={styles.badge}>
          <div style={{ ...styles.dotCore, backgroundColor: activeColor }} />

          <div style={styles.textContainer}>
            <div style={styles.brandRow}>
              <span style={styles.brandE}>E</span>
              <span style={styles.brandIE}>IE</span>
            </div>

            <div style={styles.emotionRow}>
              <div key={emotionLabel} style={styles.emotionText}>{emotionLabel}</div>
              {heartRateText ? (
                <div style={styles.hrInline}>
                  <span style={{ ...styles.hrHeart, color: activeColor }} aria-hidden>❤</span>
                  <span style={styles.hrValue}>{heartRateText}</span>
                </div>
              ) : null}
            </div>

            {!isCalibrating && confidenceText ? (
              <div style={styles.secondaryText}>{confidenceText}</div>
            ) : null}

            {!isCalibrating && cognitive ? (
              <div style={styles.secondaryText}>{getLabel(cognitive)}</div>
            ) : null}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

const styles = {
  wrapper: {
    position: "fixed",
    top: 74,
    right: 20,
    zIndex: 10000,
    width: 168,
    height: 64,
    pointerEvents: "none",
  },
  halo: {
    position: "absolute",
    inset: -6,
    borderRadius: 28,
    filter: "blur(18px)",
    transition: "opacity 1.2s ease",
  },
  badgeClip: {
    position: "absolute",
    inset: 0,
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 8px 24px rgba(2,6,23,0.45)",
  },
  blurBackground: {
    position: "absolute",
    inset: 0,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    background: "rgba(2,6,23,0.52)",
  },
  badge: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    width: "100%",
    height: "100%",
    padding: "10px 14px",
    background: "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))",
  },
  dotCore: {
    width: 7,
    height: 7,
    borderRadius: 4,
    flexShrink: 0,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
    width: "100%",
  },
  emotionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    width: "100%",
  },
  brandRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    lineHeight: 1,
  },
  brandE: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 1.4,
    color: "#E8001C",
  },
  brandIE: {
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 1.4,
    color: "rgba(255,255,255,0.82)",
  },
  emotionText: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
    minWidth: 0,
  },
  hrInline: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    flexShrink: 0,
  },
  hrHeart: {
    fontSize: 10,
    lineHeight: 1,
    animation: "eieHeartBeat 1.15s ease-in-out infinite",
    transformOrigin: "center",
  },
  hrValue: {
    color: "rgba(255,255,255,0.78)",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 0.2,
    textTransform: "lowercase",
  },
  secondaryText: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: 0.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};
